// ActivityFeed page — org-wide "what's happening on Odyssey" stream.
// Purpose: give employees a *reason to open the app daily* by showing
// what teammates are learning, posting, and sharing. Also serves as
// social proof — nothing motivates engagement like seeing peers active.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flame, Trophy, RefreshCw, Loader2, GraduationCap, MessageCircle, Newspaper } from "lucide-react";

// ---------- helpers ----------
const relTime = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const initialsOf = (name = "") => {
    const parts = String(name).trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0] || "").join("").toUpperCase() || "·";
};

const _tints = [
    "from-teal-500 to-emerald-600",
    "from-sky-500 to-indigo-600",
    "from-violet-500 to-fuchsia-600",
    "from-amber-500 to-orange-600",
    "from-rose-500 to-pink-600",
    "from-cyan-500 to-blue-600",
];
const tintFor = (id) => {
    const s = String(id || "");
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return _tints[Math.abs(h) % _tints.length];
};

const EVENT_META = {
    learning_complete: { verb: "completed", Icon: GraduationCap, colorClass: "text-emerald-600 bg-emerald-50" },
    learning_note:     { verb: "shared a learning on",           Icon: MessageCircle, colorClass: "text-[#1F7F79] bg-teal-50" },
    article_share:     { verb: "shared an article",              Icon: Newspaper, colorClass: "text-sky-700 bg-sky-50" },
};

// ---------- Event row ----------
const EventRow = ({ ev }) => {
    const meta = EVENT_META[ev.event_type] || { verb: "did something on", Icon: null, colorClass: "text-slate-600 bg-slate-100" };
    const Icon = meta.Icon;
    const isNote = ev.event_type === "learning_note";
    const notePreview = ev?.meta?.note_preview || "";

    return (
        <li className="flex items-start gap-3 p-3 hover:bg-slate-50/70 transition-colors">
            <span
                className={`h-9 w-9 rounded-full bg-gradient-to-br ${tintFor(ev.actor_user_id)} text-white text-xs font-bold inline-flex items-center justify-center flex-shrink-0 shadow-sm ring-2 ring-white`}
                title={ev.actor_name}
            >
                {initialsOf(ev.actor_name)}
            </span>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap text-sm">
                    <span className="font-semibold text-slate-900">{ev.actor_name}</span>
                    <span className="text-slate-500">{meta.verb}</span>
                    {ev.subject && (
                        <span className="font-semibold text-slate-800 truncate max-w-[420px]" title={ev.subject}>
                            {ev.subject}
                        </span>
                    )}
                </div>
                {isNote && notePreview && (
                    <div className="mt-1 text-sm text-slate-600 italic line-clamp-2">
                        “{notePreview}”
                    </div>
                )}
                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400">
                    <span className={`inline-flex items-center gap-1 px-1.5 h-4 rounded ${meta.colorClass}`}>
                        {Icon && <Icon className="h-2.5 w-2.5" />}
                        <span className="uppercase tracking-[0.08em] font-bold text-[9px]">{ev.event_type.replace(/_/g, " ")}</span>
                    </span>
                    <span>·</span>
                    <span>{relTime(ev.created_at)}</span>
                </div>
            </div>
            <span className="text-xl leading-none" aria-hidden>{ev.icon || "✨"}</span>
        </li>
    );
};

// ---------- Sidebar: your stats + top 5 leaderboard ----------
const StatsSidebar = ({ stats, leaderboard }) => {
    if (!stats?.level) return null;
    const { level, next_level, progress_pct, xp, to_next, current_streak, longest_streak } = stats;
    return (
        <aside className="space-y-4">
            {/* Your progress card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">Your progress</div>
                <div className="mt-2 flex items-center gap-3">
                    <span className="h-12 w-12 rounded-2xl bg-gradient-to-br from-teal-50 to-emerald-50 ring-1 ring-teal-100 flex items-center justify-center text-2xl">
                        {level.glyph}
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-slate-900">{level.label}</div>
                        <div className="text-[11px] text-slate-500">{xp} XP · lifetime</div>
                    </div>
                </div>
                {next_level && (
                    <>
                        <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#2EB4AC] to-emerald-500 transition-all" style={{ width: `${progress_pct}%` }} />
                        </div>
                        <div className="mt-1.5 text-[11px] text-slate-500">
                            <span className="font-semibold text-slate-700">{to_next} XP</span> to {next_level.glyph} {next_level.label}
                        </div>
                    </>
                )}

                <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-xl bg-orange-50/70 border border-orange-100 p-2.5">
                        <div className="flex items-center gap-1.5 text-orange-700 text-[10px] uppercase tracking-[0.1em] font-bold">
                            <Flame className="h-3 w-3 fill-orange-500 text-orange-500" /> Streak
                        </div>
                        <div className="mt-0.5 text-lg font-extrabold text-slate-900 tabular-nums">{current_streak}</div>
                        <div className="text-[10px] text-slate-500">best: {longest_streak}</div>
                    </div>
                    <div className="rounded-xl bg-teal-50/70 border border-teal-100 p-2.5">
                        <div className="flex items-center gap-1.5 text-[#1F7F79] text-[10px] uppercase tracking-[0.1em] font-bold">
                            <Trophy className="h-3 w-3" /> Level
                        </div>
                        <div className="mt-0.5 text-lg font-extrabold text-slate-900 truncate">{level.label}</div>
                        <div className="text-[10px] text-slate-500">next: {next_level?.label || "—"}</div>
                    </div>
                </div>
            </div>

            {/* Leaderboard card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">
                    <Trophy className="h-3 w-3 text-amber-500" /> Leaderboard
                </div>
                {leaderboard.length === 0 ? (
                    <div className="mt-3 text-xs text-slate-500 italic">Be the first — post a learning or share a story.</div>
                ) : (
                    <ol className="mt-3 space-y-2">
                        {leaderboard.slice(0, 8).map((u, i) => (
                            <li key={u.user_id} className="flex items-center gap-3">
                                <span className={`h-5 w-5 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${i === 0 ? "bg-amber-100 text-amber-800" : i === 1 ? "bg-slate-100 text-slate-700" : i === 2 ? "bg-orange-100 text-orange-800" : "bg-slate-50 text-slate-500"}`}>
                                    {i + 1}
                                </span>
                                <span className={`h-7 w-7 rounded-full bg-gradient-to-br ${tintFor(u.user_id)} text-white text-[10px] font-bold inline-flex items-center justify-center shadow-sm flex-shrink-0`}>
                                    {initialsOf(u.name || u.email)}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-slate-900 truncate">{u.name || u.email}</div>
                                    <div className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                                        <span>{u.level?.glyph}</span>
                                        <span>{u.level?.label}</span>
                                    </div>
                                </div>
                                <div className="text-xs font-bold text-[#1F7F79] tabular-nums">{u.xp}</div>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </aside>
    );
};

// ---------- LevelLadder — the "how to reach the top" explainer ----------
// Full ladder of levels with thresholds + user's marker; then a clear
// list of every XP-earning action + its cap. The whole point of this
// card is to answer "what do I do to level up?" in one screen.
const XP_ACTIONS = [
    {
        icon: "🎓",
        label: "Complete a learning resource",
        xp: 50,
        note: "First time only per resource",
        cta: { to: "/learning", text: "Open Learning Hub" },
    },
    {
        icon: "💡",
        label: "Post a team learning (note on a resource)",
        xp: 20,
        note: "Anyone in the org can read it",
        cta: { to: "/learning", text: "Add a learning" },
    },
    {
        icon: "📰",
        label: "Share a news article via Odyssey",
        xp: 10,
        note: "Awarded when you copy the link or send via WhatsApp / LinkedIn / X / Email",
        cta: { to: "/news", text: "Browse News" },
    },
    {
        icon: "📖",
        label: "Read a news story",
        xp: 5,
        note: "Capped at 1× per day",
        cta: { to: "/news", text: "Catch up on News" },
    },
];

const LevelLadder = ({ stats }) => {
    if (!stats?.levels) return null;
    const levels = stats.levels;
    const xp = stats.xp || 0;
    const curKey = stats.level.key;

    // Total earnable in a "typical" active day — helps set expectations.
    // Not a hard fact, just a rough estimate: 1 completion + 1 note +
    // 1 share + 1 read = 85 XP. We'll surface it as guidance.
    const dailyEstimate = 85;

    return (
        <div className="bg-gradient-to-br from-teal-50/70 via-white to-emerald-50/40 rounded-2xl border border-teal-100 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#1F7F79] font-black">Your climb</div>
                    <h2 className="mt-0.5 text-lg font-heading font-black text-slate-900">
                        {stats.next_level ? (
                            <>Reach <span className="text-[#1F7F79]">{stats.next_level.glyph} {stats.next_level.label}</span> in <span className="tabular-nums">{stats.to_next}</span> XP</>
                        ) : (
                            <>You&apos;re at the top of the ladder 🏆</>
                        )}
                    </h2>
                </div>
                {stats.next_level && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 bg-white/80 border border-slate-200 rounded-full px-2 py-1">
                        ~{dailyEstimate} XP/day possible
                    </span>
                )}
            </div>

            {/* Level track — 6 steps connected by a progress rail */}
            <div className="relative mt-4 mb-2">
                {/* Rail base */}
                <div className="absolute left-0 right-0 top-4 h-1 rounded-full bg-slate-200" />
                {/* Rail progress */}
                <div
                    className="absolute left-0 top-4 h-1 rounded-full bg-gradient-to-r from-[#2EB4AC] to-emerald-500 transition-all"
                    style={{ width: (() => {
                        // How far are we across the ladder? Interpolate between the
                        // reached level's slot and the next slot by the intra-level %.
                        const idx = levels.findIndex((l) => l.key === curKey);
                        if (idx < 0) return "0%";
                        const perSlot = 100 / (levels.length - 1);
                        const base = idx * perSlot;
                        const bonus = stats.next_level ? (perSlot * (stats.progress_pct || 0) / 100) : 0;
                        return `${Math.min(100, base + bonus)}%`;
                    })() }}
                />
                <ol className="relative flex justify-between">
                    {levels.map((l) => {
                        const reached = xp >= l.min_xp;
                        const isCurrent = l.key === curKey;
                        return (
                            <li key={l.key} className="flex flex-col items-center gap-1 min-w-0" style={{ width: `${100 / levels.length}%` }}>
                                <span
                                    className={`relative h-9 w-9 rounded-full border-2 flex items-center justify-center text-base transition-all
                                        ${reached
                                            ? "bg-white border-[#2EB4AC] shadow-sm"
                                            : "bg-slate-50 border-slate-200 text-slate-400"}
                                        ${isCurrent ? "ring-4 ring-[#2EB4AC]/25 scale-110" : ""}`}
                                    title={`${l.label} · ${l.min_xp}+ XP`}
                                >
                                    {reached ? l.glyph : <span className="opacity-50">{l.glyph}</span>}
                                </span>
                                <span className={`text-[10px] font-bold uppercase tracking-[0.06em] leading-tight text-center ${isCurrent ? "text-[#1F7F79]" : reached ? "text-slate-700" : "text-slate-400"}`}>
                                    {l.label}
                                </span>
                                <span className="text-[9px] text-slate-500 tabular-nums leading-none">
                                    {l.min_xp === 0 ? "start" : `${l.min_xp}+`}
                                </span>
                            </li>
                        );
                    })}
                </ol>
            </div>

            {/* Earn actions — how to actually get XP */}
            <div className="mt-4 pt-4 border-t border-teal-100/60">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#1F7F79] font-black mb-2">How to earn XP</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="xp-actions-grid">
                    {XP_ACTIONS.map((a) => (
                        <li key={a.label} className="group flex items-start gap-2.5 p-2.5 rounded-lg bg-white border border-slate-200 hover:border-[#2EB4AC]/50 hover:shadow-sm transition-all">
                            <span className="text-lg leading-none flex-shrink-0 mt-0.5">{a.icon}</span>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2">
                                    <div className="text-xs font-bold text-slate-900 truncate">{a.label}</div>
                                    <span className="text-[11px] font-black text-[#1F7F79] whitespace-nowrap">+{a.xp} XP</span>
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5">{a.note}</div>
                                <Link
                                    to={a.cta.to}
                                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-[#1F7F79] hover:underline"
                                >
                                    {a.cta.text} →
                                </Link>
                            </div>
                        </li>
                    ))}
                </ul>
                <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-600 bg-white/70 border border-slate-200 rounded-lg p-2.5">
                    <Flame className="h-3.5 w-3.5 text-orange-500 fill-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <strong>Streak bonus:</strong> any XP-earning action counts as active for the day. Miss a day and your streak resets — one small action keeps it alive.
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---------- Main page ----------
const ActivityPage = () => {
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [feed, s, lb] = await Promise.all([
                api.get("/activity/feed?limit=40"),
                api.get("/me/stats"),
                api.get("/leaderboard?limit=10"),
            ]);
            setItems(feed.data?.items || []);
            setCursor(feed.data?.next_cursor || "");
            setStats(s.data);
            setLeaderboard(lb.data?.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load activity");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const loadMore = async () => {
        if (!cursor) return;
        setLoadingMore(true);
        try {
            const r = await api.get(`/activity/feed?limit=40&before=${encodeURIComponent(cursor)}`);
            setItems((prev) => [...prev, ...(r.data?.items || [])]);
            setCursor(r.data?.next_cursor || "");
        } finally { setLoadingMore(false); }
    };

    // Simple day-grouping so the timeline reads naturally.
    const groups = useMemo(() => {
        const buckets = new Map();
        const today = new Date();
        const dayKey = (iso) => {
            if (!iso) return "unknown";
            const d = new Date(iso);
            const diffDays = Math.floor((today - d) / 86400000);
            if (diffDays < 1) return "Today";
            if (diffDays < 2) return "Yesterday";
            return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        };
        for (const ev of items) {
            const k = dayKey(ev.created_at);
            if (!buckets.has(k)) buckets.set(k, []);
            buckets.get(k).push(ev);
        }
        return Array.from(buckets.entries());
    }, [items]);

    return (
        <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="activity-page">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="font-heading font-black text-2xl sm:text-3xl text-slate-900">Activity</h1>
                    <p className="text-sm text-slate-500 mt-0.5">What everyone&apos;s learning and sharing on Odyssey.</p>
                </div>
                <button
                    onClick={load}
                    data-testid="activity-refresh"
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
                {/* Feed column */}
                <div className="space-y-4">
                    <LevelLadder stats={stats} />

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="p-8 flex flex-col items-center justify-center text-slate-500 gap-2">
                            <Loader2 className="h-6 w-6 animate-spin text-[#2EB4AC]" />
                            <span className="text-sm">Loading team activity…</span>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="text-4xl mb-3">🌱</div>
                            <div className="text-sm font-semibold text-slate-900">The feed&apos;s quiet today</div>
                            <div className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                                Complete a learning, share a news article, or post your first team learning — you&apos;ll show up here first.
                            </div>
                            <Link
                                to="/learning"
                                className="mt-4 inline-flex items-center h-9 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-bold"
                            >
                                Explore learnings
                            </Link>
                        </div>
                    ) : (
                        <div>
                            {groups.map(([day, evs]) => (
                                <div key={day}>
                                    <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold bg-white sticky top-0 z-10 border-b border-slate-100">
                                        {day}
                                    </div>
                                    <ul className="divide-y divide-slate-100">
                                        {evs.map((ev) => <EventRow key={ev.id} ev={ev} />)}
                                    </ul>
                                </div>
                            ))}
                            {cursor && (
                                <div className="p-3 text-center border-t border-slate-100">
                                    <button
                                        onClick={loadMore}
                                        disabled={loadingMore}
                                        data-testid="activity-load-more"
                                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-[#1F7F79] hover:bg-teal-50 disabled:opacity-50"
                                    >
                                        {loadingMore && <Loader2 className="h-3 w-3 animate-spin" />}
                                        Load more
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    </div>
                </div>

                <StatsSidebar stats={stats} leaderboard={leaderboard} />
            </div>
        </div>
    );
};

export default ActivityPage;
